import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { api, fmtDate, kr, type Asset } from '../api';
import { useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton, Stat } from '../ui';

const CATEGORIES = ['Lánsstokkar', 'Borðspil', 'Spilabúnaður', 'Húsgögn', 'Tæki', 'Annað'];
const CONDITIONS = ['Nýtt', 'Gott', 'Slitið', 'Ónýtt'];

/* A fixed list rather than free text: grant reports ask what a specific
   fund paid for, and that only works if the spelling is consistent. */
const FUNDERS = [
  'Eigið fé',
  'Uppbyggingarsjóður Vestfjarða',
  'Æskulýðssjóður',
  'Menningarstyrkur Ísafjarðarbæjar',
  'Félagsgjöld',
  'Gjöf',
  'Annað',
];

const OWN = 'Eigið fé';

export default function Eignir() {
  const { assets, refresh } = useData();

  const [cat, setCat] = useState<string>('allt');
  const [name, setName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [quantity, setQuantity] = useState('1');
  const [condition, setCondition] = useState(CONDITIONS[1]);
  const [acquired, setAcquired] = useState(new Date().toISOString().slice(0, 10));
  const [cost, setCost] = useState('');
  const [fundedBy, setFundedBy] = useState(FUNDERS[0]);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Asset>>({});

  const shown = cat === 'allt' ? assets : assets.filter((a) => a.category === cat);
  const totalUnits = assets.reduce((n, a) => n + a.quantity, 0);
  const bookValue = assets.reduce((n, a) => n + a.cost_isk, 0);
  const grantValue = assets
    .filter((a) => a.funded_by && a.funded_by !== OWN)
    .reduce((n, a) => n + a.cost_isk, 0);
  const worn = assets.filter((a) => a.condition === 'Slitið' || a.condition === 'Ónýtt').length;

  const byCategory = CATEGORIES.map((c) => ({
    c,
    n: assets.filter((a) => a.category === c).reduce((n, a) => n + a.quantity, 0),
  })).filter((x) => x.n > 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.addAsset({
        name: name.trim(),
        category,
        quantity: Math.max(1, Number(quantity) || 1),
        condition,
        acquired_date: acquired || null,
        cost_isk: Number(cost) || 0,
        funded_by: fundedBy,
        location: location.trim(),
        note: note.trim(),
      });
      setName('');
      setQuantity('1');
      setCost('');
      setLocation('');
      setNote('');
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(a: Asset) {
    setEditId(a.id);
    setDraft({
      name: a.name,
      quantity: a.quantity,
      condition: a.condition,
      cost_isk: a.cost_isk,
      funded_by: a.funded_by,
      location: a.location,
      note: a.note,
    });
  }

  function cancelEdit() {
    setEditId(null);
    setDraft({});
  }

  async function saveEdit() {
    if (editId === null || !String(draft.name ?? '').trim()) return;
    await api.updateAsset(editId, draft);
    cancelEdit();
    await refresh();
  }

  function onKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }

  async function remove(id: number, what: string) {
    if (!window.confirm(`Eyða "${what}" úr eignaskrá?`)) return;
    await api.delAsset(id);
    if (editId === id) cancelEdit();
    await refresh();
  }

  return (
    <div className="view">
      <div className="stats">
        <Stat label="Hlutir alls" value={totalUnits} />
        <Stat label="Bókfært virði" value={kr(bookValue)} gold />
        <Stat label="Styrkfjármagnað" value={kr(grantValue)} />
        <Stat label="Eigið fé" value={kr(bookValue - grantValue)} />
        <Stat label="Tegundir" value={byCategory.length} />
        <Stat label="Slitið eða ónýtt" value={worn} />
      </div>

      <div className="g-two">
        <section className="card col-8">
          <div className="card-h">
            <h2>Eignaskrá</h2>
            <span className="lbl">{assets.length} færslur</span>
          </div>

          <div className="filters">
            <button
              type="button"
              className={`filt${cat === 'allt' ? ' on' : ''}`}
              aria-pressed={cat === 'allt'}
              onClick={() => setCat('allt')}
            >
              Allt<span className="n">{totalUnits}</span>
            </button>
            {byCategory.map(({ c, n }) => (
              <button
                key={c}
                type="button"
                className={`filt${cat === c ? ' on' : ''}`}
                aria-pressed={cat === c}
                onClick={() => setCat(c)}
              >
                {c}
                <span className="n">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <Empty
              title="Ekkert skráð enn"
              body="Skráðu lánsstokka, borðspil, borð og annað sem félagið á. Styrkfjármagnaður búnaður þarf að vera skráður eign félagsins."
            />
          ) : (
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Hlutur</th>
                    <th>Fjöldi</th>
                    <th>Ástand</th>
                    <th>Fjármagnað af</th>
                    <th>Virði</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => {
                    const editing = editId === a.id;
                    return (
                      <tr key={a.id} className={editing ? 'editing' : undefined}>
                        <td>
                          {editing ? (
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <input
                                className="tin"
                                value={String(draft.name ?? '')}
                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                onKeyDown={onKey}
                                aria-label="Heiti"
                                placeholder="Heiti"
                                autoFocus
                              />
                              <input
                                className="tin"
                                value={String(draft.location ?? '')}
                                onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                                onKeyDown={onKey}
                                aria-label="Geymslustaður"
                                placeholder="Geymslustaður"
                              />
                              <input
                                className="tin"
                                value={String(draft.note ?? '')}
                                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                                onKeyDown={onKey}
                                aria-label="Athugasemd"
                                placeholder="Athugasemd"
                              />
                            </span>
                          ) : (
                            <>
                              <div className="ttl2">{a.name}</div>
                              <span className="meta">
                                {a.category}
                                {a.location ? ` · ${a.location}` : ''}
                                {a.acquired_date ? ` · ${fmtDate(a.acquired_date)}` : ''}
                              </span>
                              {a.note && <div className="meta">{a.note}</div>}
                            </>
                          )}
                        </td>

                        <td className="n" data-label="Fjöldi">
                          {editing ? (
                            <input
                              className="tin"
                              type="number"
                              min={1}
                              style={{ minWidth: '4.5rem' }}
                              value={String(draft.quantity ?? 1)}
                              onChange={(e) =>
                                setDraft({ ...draft, quantity: Number(e.target.value) || 1 })
                              }
                              onKeyDown={onKey}
                              aria-label="Fjöldi"
                            />
                          ) : (
                            a.quantity
                          )}
                        </td>

                        <td data-label="Ástand">
                          {editing ? (
                            <select
                              className="tin"
                              value={String(draft.condition ?? 'Gott')}
                              onChange={(e) => setDraft({ ...draft, condition: e.target.value })}
                              aria-label="Ástand"
                            >
                              {CONDITIONS.map((c) => (
                                <option key={c}>{c}</option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className={
                                a.condition === 'Ónýtt'
                                  ? 'chip guest'
                                  : a.condition === 'Slitið'
                                    ? 'chip'
                                    : 'chip ok'
                              }
                            >
                              {a.condition}
                            </span>
                          )}
                        </td>

                        <td data-label="Fjármagnað af">
                          {editing ? (
                            <select
                              className="tin"
                              value={String(draft.funded_by ?? OWN)}
                              onChange={(e) => setDraft({ ...draft, funded_by: e.target.value })}
                              aria-label="Fjármagnað af"
                            >
                              {FUNDERS.map((f) => (
                                <option key={f}>{f}</option>
                              ))}
                            </select>
                          ) : a.funded_by && a.funded_by !== OWN ? (
                            <span className="chip gold">{a.funded_by}</span>
                          ) : (
                            <span className="meta">{OWN}</span>
                          )}
                        </td>

                        <td className="n" data-label="Virði">
                          {editing ? (
                            <input
                              className="tin"
                              type="number"
                              min={0}
                              step={1}
                              style={{ minWidth: '6rem' }}
                              value={String(draft.cost_isk ?? 0)}
                              onChange={(e) =>
                                setDraft({ ...draft, cost_isk: Number(e.target.value) || 0 })
                              }
                              onKeyDown={onKey}
                              aria-label="Virði"
                            />
                          ) : (
                            <>
                              <div>{kr(a.cost_isk)}</div>
                              {a.quantity > 1 && a.cost_isk > 0 && (
                                <div className="meta">
                                  {a.quantity} × {kr(Math.round(a.cost_isk / a.quantity))}/stk.
                                </div>
                              )}
                            </>
                          )}
                        </td>

                        <td className="act">
                          <span className="row-acts">
                            {editing ? (
                              <>
                                <IconButton tone="ok" label="Vista" onClick={() => void saveEdit()}>
                                  <Icon.save />
                                </IconButton>
                                <IconButton label="Hætta við" onClick={cancelEdit}>
                                  <Icon.cancel />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton label={`Breyta ${a.name}`} onClick={() => beginEdit(a)}>
                                  <Icon.pencil />
                                </IconButton>
                                <DeleteButton
                                  label={`Eyða ${a.name}`}
                                  onClick={() => void remove(a.id, a.name)}
                                />
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>Ný eign</h2>
          </div>

          {editId !== null && (
            <div className="alert" style={{ marginBottom: 14 }}>
              Þú ert að breyta færslu í töflunni. Reitirnir hér búa til <strong>nýja</strong> eign —
              breytingarnar gerast í línunni sjálfri.
            </div>
          )}

          <form className="form" onSubmit={submit}>
            <div className="fld">
              <label className="lbl" htmlFor="a-name">Hlutur</label>
              <input
                id="a-name"
                type="text"
                placeholder="Lánsstokkur — Commander"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="a-cat">Flokkur</label>
                <select id="a-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="a-qty">Fjöldi</label>
                <input
                  id="a-qty"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="a-cost">Virði alls (kr.)</label>
                <input
                  id="a-cost"
                  type="number"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="a-date">Keypt</label>
                <input
                  id="a-date"
                  type="date"
                  value={acquired}
                  onChange={(e) => setAcquired(e.target.value)}
                />
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="a-funder">Fjármagnað af</label>
              <select id="a-funder" value={fundedBy} onChange={(e) => setFundedBy(e.target.value)}>
                {FUNDERS.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="a-cond">Ástand</label>
                <select id="a-cond" value={condition} onChange={(e) => setCondition(e.target.value)}>
                  {CONDITIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="a-loc">Geymslustaður</label>
                <input
                  id="a-loc"
                  type="text"
                  placeholder="Hjá Emil"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="a-note">Athugasemd</label>
              <input
                id="a-note"
                type="text"
                placeholder="Pakkaverð — 5 stokkar í settinu"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <button className="btn" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Vista…' : 'Skrá eign'}
            </button>
          </form>

          <p style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            Þetta er skrá yfir eignir, ekki bókhald. Peningarnir fóru út í gegnum Fjármál eða á
            viðburð — virðið hér telst ekki tvisvar í stöðu sjóðsins.
          </p>
          <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--ink-2)' }}>Fjármagnað af</strong> skiptir mestu máli:
            styrktarsjóðir spyrja hvað peningarnir keyptu, og búnaður sem styrkur greiddi þarf að
            vera áfram eign félagsins — líka ef búð opnar síðar.
          </p>
        </section>
      </div>
    </div>
  );
}
